import { PriceMath, Whirlpool } from '@renec-foundation/nemoswap-sdk'
import Decimal from 'decimal.js'
import { useCallback, useEffect, useState } from 'react'

import { toResult } from '@/utils/helpers'

export type PriceRange = [Decimal, Decimal]
export type TickRange = [number, number]

const usePriceRange = (
  pool?: Whirlpool,
  defaultPriceRange: PriceRange = [new Decimal(0), new Decimal(0)],
) => {
  const [priceRange, setPriceRange] = useState(defaultPriceRange)
  const [tickRange, setTickRange] = useState<TickRange | null>(null)

  const handleSetPriceRange = useCallback(
    (newRange: PriceRange) => {
      if (newRange[1].lt(newRange[0])) {
        [newRange[0], newRange[1]] = [newRange[1], newRange[0]]
      }

      const roundPrice = (price: Decimal) => {
        const decimalsA = pool?.getTokenAInfo().decimals ?? 0
        const decimalsB = pool?.getTokenBInfo().decimals ?? 0
        const tickSpacing = pool?.getData().tickSpacing ?? 1

        const initializableTickIndex = PriceMath.priceToInitializableTickIndex(
          price,
          decimalsA,
          decimalsB,
          tickSpacing,
        )
        const roundedPrice = PriceMath.tickIndexToPrice(
          initializableTickIndex,
          decimalsA,
          decimalsB,
        )

        return [roundedPrice, initializableTickIndex] as const
      }

      const [price0, tick0] = toResult(() => roundPrice(newRange[0]))[1] ?? [new Decimal(0), null]
      const [price1, tick1] = toResult(() => roundPrice(newRange[1]))[1] ?? [new Decimal(0), null]

      setPriceRange([price0, price1])
      setTickRange(tick0 === null || tick1 === null ? null : [tick0, tick1])
    },
    [pool],
  )

  useEffect(() => {
    handleSetPriceRange(priceRange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool])

  return { priceRange, tickRange, setPriceRange: handleSetPriceRange }
}

export default usePriceRange
